import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  getAdditionalUserInfo,
  updateProfile,
  GoogleAuthProvider,
  OAuthProvider,
  signOut as firebaseSignOut,
  deleteUser,
} from 'firebase/auth';
import { doc, setDoc, getDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../utils/firebase';

const AuthContext = createContext();

const SYNC_KEYS = [
  'quizScores',
  'learnedWords',
  'failedQuestions',
  'studyStreak',
  'bookmarkedWords',
  'userLanguage',
];

const getUserProfile = (firebaseUser) => {
  const googleProvider = firebaseUser.providerData?.find(
    (p) => p.providerId === 'google.com'
  );
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email || googleProvider?.email || null,
    displayName: firebaseUser.displayName || googleProvider?.displayName || null,
    photoURL: firebaseUser.photoURL || googleProvider?.photoURL || null,
  };
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(getUserProfile(firebaseUser));
        setLoading(false);
        await pullFromCloud(firebaseUser.uid);
        await pushToCloud(firebaseUser.uid, true);
      } else {
        setUser(null);
        setLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  const pushToCloud = async (uid, onlyNonEmpty = false) => {
    try {
      const data = {};
      for (const key of SYNC_KEYS) {
        const value = localStorage.getItem(key);
        if (value !== null) {
          if (onlyNonEmpty) {
            try {
              const parsed = JSON.parse(value);
              if (
                parsed === null ||
                parsed === '' ||
                (Array.isArray(parsed) && parsed.length === 0) ||
                (typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length === 0)
              ) {
                continue;
              }
            } catch {
              if (value === '' || value === 'null' || value === 'undefined') {
                continue;
              }
            }
          }
          data[key] = value;
        }
      }
      if (Object.keys(data).length === 0) return;
      data.updatedAt = serverTimestamp();
      await setDoc(doc(db, 'users', uid), data, { merge: true });
    } catch (error) {
      console.error('Error pushing to cloud:', error);
    }
  };

  const pullFromCloud = async (uid) => {
    try {
      const snapshot = await getDoc(doc(db, 'users', uid));
      if (snapshot.exists()) {
        const cloudData = snapshot.data();
        for (const key of SYNC_KEYS) {
          if (cloudData[key] !== undefined && cloudData[key] !== null) {
            localStorage.setItem(key, cloudData[key]);
          }
        }
      }
    } catch (error) {
      console.error('Error pulling from cloud:', error);
    }
  };

  const syncData = async () => {
    if (!user) return;
    try {
      await pushToCloud(user.uid);
      await pullFromCloud(user.uid);
      alert('Your data has been synced to the cloud.');
    } catch (error) {
      console.error('Sync error:', error);
      alert('Could not sync data. Please try again.');
    }
  };

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, provider);
      const additionalInfo = getAdditionalUserInfo(result);
      const profile = additionalInfo?.profile;
      const googleProvider = result.user.providerData?.find(
        (p) => p.providerId === 'google.com'
      );
      const displayName = result.user.displayName || googleProvider?.displayName || profile?.name || null;
      const photoURL = result.user.photoURL || googleProvider?.photoURL || profile?.picture || null;
      if (!result.user.displayName || !result.user.photoURL) {
        await updateProfile(result.user, { displayName, photoURL });
      }
      setUser({
        uid: result.user.uid,
        email: result.user.email,
        displayName,
        photoURL,
      });
      await pullFromCloud(result.user.uid);
      await pushToCloud(result.user.uid, true);
      return result.user;
    } catch (error) {
      if (error.code !== 'auth/popup-closed-by-user') {
        console.error('Google sign-in error:', error);
        alert('Google Sign-In failed: ' + error.message);
      }
      return null;
    }
  };

  const signInWithApple = async () => {
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      const result = await signInWithPopup(auth, provider);
      await pullFromCloud(result.user.uid);
      await pushToCloud(result.user.uid, true);
      return result.user;
    } catch (error) {
      if (error.code !== 'auth/popup-closed-by-user') {
        console.error('Apple sign-in error:', error);
        alert('Apple Sign-In failed: ' + error.message);
      }
      return null;
    }
  };

  const signOut = async () => {
    try {
      if (user) {
        await pushToCloud(user.uid);
      }
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Sign out error:', error);
      alert('Failed to sign out');
    }
  };

  const deleteAccount = async () => {
    try {
      if (user) {
        await deleteDoc(doc(db, 'users', user.uid));
        await deleteUser(user);
      }
    } catch (error) {
      console.error('Delete account error:', error);
      if (error.code === 'auth/requires-recent-login') {
        alert('Please sign out and sign in again before deleting your account.');
      } else {
        alert('Failed to delete account');
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signInWithGoogle,
        signInWithApple,
        signOut,
        deleteAccount,
        syncData,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
